import React, { useRef, useState } from "react";
import { MarkunreadMailboxOutlined, MoreHoriz } from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { lettersActions } from "@_actions/letters.actions";
import { PingenLetterStatusLabels } from "@_configs/LetterSendingConfig";
import { templateService } from "@_services/template.service";
import { emailConstants } from "@_constants/email_flows.constants";

import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";
import MailPopup from "@_components/popups/MailPopup/MailPopup";

import { useConfirmPopup } from "@_hooks/useConfirmPopup";
import CardShell from "../core/CardShell";
import styles from "../CaseActivityCard.module.css";

/**
 * LetterActivityCard
 *
 * Renders CASEACTIVITY_TYPE_LETTER_SENT rows.
 *
 * MoreHoriz menu actions:
 *   - View
 *   - Download
 *   - Resend (only shown when status is action_required and provider_id exists)
 */
export default function LetterActivityCard({
  activity,
  caseData,
  resumeAutomation,
}) {
  const dispatch = useDispatch();
  const emailSetup = useSelector(
    (s) => s.emailSetupReducer.email_setup.active_email_setup,
  );

  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [emailData, setEmailData] = useState({});
  const [openMailPopup, setOpenMailPopup] = useState(false);

  const confirm = useConfirmPopup();

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleDownload = (action) => {
    dispatch(
      lettersActions.downloadLetter(
        activity.case_id,
        activity.content.letter_id,
        action,
      ),
    );
    setMenuOpen(false);
  };

  const handleResendPrompt = () => {
    setMenuOpen(false);
    confirm.prompt(
      `Make sure you fixed the issue "${activity?.content?.fail_reason}" before resending.\nAre you sure you want to resend the letter?`,
      handleResendRender,
    );
  };

  const handleResendRender = () => {
    confirm.close();
    templateService
      .getCaseTemplate(caseData.id, activity.content.template_id)
      .then(
        (template) => {
          const contact = caseData.opponent_contact_details;
          const to = contact
            ? [
                contact.name && `${contact.name}, `,
                contact.company && `${contact.company}, `,
                contact.address_line_1 && `${contact.address_line_1}, `,
                contact.address_line_2 && `${contact.address_line_2}, `,
                contact.postal_code && `${contact.postal_code} `,
                contact.city && `${contact.city}, `,
                contact.country && contact.country,
              ]
                .filter(Boolean)
                .join("")
            : (contact?.email ?? "");

          setEmailData({
            content: template.content,
            name: template.name ?? "",
            subject: template.subject,
            opponentEmail: to,
            type: emailConstants.TEMPLATE_TYPE_LETTER,
            template_id: activity.content.template_id,
            oldFailedLetterId: activity.content.letter_id,
          });
          setOpenMailPopup(true);
        },
        (error) => alert(error),
      );
  };

  // ── Content preview ────────────────────────────────────────────────────────

  const letterStatus = activity?.content?.letter_status;
  const statusLabel =
    letterStatus === "action_required" && !activity.content.provider_id
      ? PingenLetterStatusLabels.resent
      : (PingenLetterStatusLabels[letterStatus] ?? "Unknown");

  const contentPreview = (
    <>
      {letterStatus && (
        <div className={styles.letter_status_flex_row}>
          <div className={styles.letter_status_text}>Status: {statusLabel}</div>
          <div className={styles.letter_fail_reason}>
            {activity?.content?.fail_reason}
          </div>
        </div>
      )}
      <div
        className={styles.content_subject_ellipsis}
        title={activity.content.template_subject}
      >
        {activity.content.template_name || activity.content.subject}
      </div>
    </>
  );

  const showResend =
    letterStatus === "action_required" &&
    PingenLetterStatusLabels.action_required &&
    activity.content.provider_id;

  const openerColumn = (
    <>
      <MoreHoriz
        className={styles.edit_icon}
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen(true);
        }}
        ref={anchorRef}
        aria-controls={menuOpen ? "letter-menu" : undefined}
        aria-haspopup="true"
      />
      <Menu
        id="letter-menu"
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem data-testid="menu-item-view" onClick={() => handleDownload("view")}>View</MenuItem>
        <MenuItem data-testid="menu-item-download" onClick={() => handleDownload("download")}>Download</MenuItem>
        {showResend && <MenuItem data-testid="menu-item-resend" onClick={handleResendPrompt}>Resend</MenuItem>}
      </Menu>
    </>
  );

  return (
    <>
      <CardShell
        activity={activity}
        icon={<MarkunreadMailboxOutlined />}
        typeText={<span>Letter</span>}
        typeSubText={activity.user_name}
        contentPreview={contentPreview}
        openerColumn={openerColumn}
      />

      <MailPopup
        title={`mail preview: ${emailData.name}`}
        open={openMailPopup}
        close={() => setOpenMailPopup(false)}
        data={emailData}
        caseId={activity.case_id}
        emailSetup={emailSetup}
        activity={activity}
        isReplyMode={false}
        resumeAutomation={resumeAutomation}
      />

      <AreYouSurePopup
        open={confirm.open}
        close={confirm.close}
        onClick={confirm.action}
      >
        <p className={styles.are_you_sure_preline}>{confirm.text}</p>
      </AreYouSurePopup>
    </>
  );
}
